import * as sentryNextJs from '@sentry/nextjs';

const { captureException } = sentryNextJs;

import { getEnv } from './env';
import { FetchResponse } from '../types/fetchResponse';

let errorListeners: ((errorObj: APIError) => void)[] = [];

export function registerFetchErrorListener(listener: (errorObj: APIError) => void) {
  errorListeners.push(listener);
}

export function deregisterFetchErrorListener(listener: (errorObj: APIError) => void) {
  errorListeners = errorListeners.filter((item) => item !== listener);
}

export const e2eTestHeaders: Record<string, string> = {};

const baseUrl = getEnv(
  'NEXT_PUBLIC_CLOUDQUERY_API_BASE_URL',
  process.env.NEXT_PUBLIC_CLOUDQUERY_API_BASE_URL,
  'http://localhost:4444',
);

export interface APIError {
  data: any;
  message: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  payload: any;
  status: number;
}

/**
 * Since react-query is generated via orval we cannot
 * provide default value to pageParam. Therefore, we assign
 * default value to page param here
 */
function handlePageParam(params: Record<string, any> | undefined) {
  const updatedParams = { ...params };
  if (Object.hasOwn(updatedParams, 'page')) {
    updatedParams.page = updatedParams.page || 1;
  }

  return updatedParams;
}

export interface CustomFetchConfig {
  credentials?: 'include' | 'omit' | 'same-origin';
  data?: unknown;
  headers?: Record<string, string>;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  params?: Record<string, any>;
  signal?: AbortSignal;
  streamData?: boolean;
  url: string;
}

class ApiError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

export const customFetch = async <T>(config: CustomFetchConfig): Promise<FetchResponse<T>> => {
  const { data, headers, method, params, signal, streamData, url } = config;

  const searchParams = handlePageParam(params);
  let searchParamsStr = '';
  for (const [key, value] of Object.entries(searchParams || {})) {
    searchParamsStr += Array.isArray(value)
      ? `${(value as string[]).map((v) => `${key}=${encodeURIComponent(v)}`).join('&')}&`
      : `${key}=${encodeURIComponent(value as string)}&`;
  }

  const urlWithSearchParams = `${url}${searchParamsStr ? `?${searchParamsStr}` : ''}`;
  const fullUrl = `${url.startsWith('http') ? '' : baseUrl}${urlWithSearchParams}`;

  try {
    const response = await fetch(fullUrl, {
      method,
      ...(data ? { body: JSON.stringify(data) } : {}),
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...headers,
        ...(process.env.NEXT_PUBLIC_PLAYWRIGHT_TEST === 'true' ? e2eTestHeaders : {}),
      },
      next: {
        tags: ['every'],
      },
      signal,
    });

    const responseData: any = streamData ? {} : await response.json().catch(() => ({}));

    if (response.ok && response.status !== 205) {
      return {
        data: responseData as T,
        headers: response.headers,
        response,
        status: response.status,
        statusText: response.statusText,
      };
    }

    throw {
      data: streamData ? await response.json().catch(() => ({})) : responseData,
      headers: response.headers,
      isCustom: true,
      message: 'There was an error during the API request',
      payload: data,
      status: response.status,
    };
  } catch (error) {
    const isUserEndpointError =
      new URL(fullUrl, 'https://cloudquery.io').pathname.endsWith('/user') && error.status === 401;

    if (!isUserEndpointError && error.name !== 'AbortError') {
      // eslint-disable-next-line no-console
      console.error('captured API error:', error);
      const extra =
        'isCustom' in error && error.isCustom
          ? {
              data: error.data,
              message: error.message,
              method,
              payload: error.payload,
              status: error.status,
              url: fullUrl,
            }
          : {
              error: {
                message: error.message,
                method,
                name: error.name,
                payload: data,
                stack: error.stack,
                url: fullUrl,
              },
            };
      captureException(new ApiError(`API Error for ${fullUrl}: ${error.message}`), {
        extra,
      });
    }

    for (const callback of errorListeners) {
      callback(error);
    }

    throw error;
  }
};

export default customFetch;
